import { isEqual } from 'lodash';
import { createSelector } from 'reselect';

import { splitFullName } from 'commonUtils/formatters/splitFullName';
import { createScoresQueryData, isScoresQueryDataReady } from 'commonUtils/scoring/createScoresQueryData';

import { selectContactsInfoData, selectPassportInfoData } from 'entities/creditForm';
import type { TScoringRequest } from 'entities/scoring';
import { getUserScoreSelector } from 'entities/scoring';

type TUpdateScoreValueState = { queryData: TScoringRequest; isNeedUpdateScore: boolean };

const sameUserData = (queryData: TScoringRequest, scoredUserData: string) => {
  if (!scoredUserData) {
    return false;
  }

  try {
    const restoredScoredData = JSON.parse(scoredUserData);

    return isEqual(queryData, restoredScoredData);
  } catch (error) {
    return false;
  }
};

export const updateScoreValueSelector = createSelector(
  getUserScoreSelector,
  selectPassportInfoData,
  selectContactsInfoData,
  (scoreData, passport, contacts): TUpdateScoreValueState => {
    const {
      birthDate,
      passportNumber,
      passportIssuedBy,
      registrationAddress,
      unitCode,
      residenceAddress,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      residenceAddressIsRegistration,
      passportIssueDate,
      birthPlace,
      fullName,
    } = passport;
    const { email, mobilePhone } = contacts;
    const { firstName, lastName, middleName } = splitFullName(fullName?.value || '');
    const { scoredUserData, score } = scoreData;

    const queryData = createScoresQueryData({
      birthDate: birthDate || '',
      birthPlace,
      email,
      firstName,
      lastName,
      middleName: middleName || undefined,
      isResidenceAddressMatch: residenceAddressIsRegistration,
      passportIssueDate: passportIssueDate || '',
      passportIssuedBy,
      passportNumber: (passportNumber || '').substring(4),
      passportSerial: (passportNumber || '').substring(0, 4),
      phone: mobilePhone,
      registrationAddress: registrationAddress?.value || '',
      residenceAddress: residenceAddress?.value || '',
      unitCode,
    });

    const isNeedUpdateScore = isScoresQueryDataReady(queryData) && (!score || !sameUserData(queryData, scoredUserData));

    return {
      isNeedUpdateScore,
      queryData,
    };
  },
);
